import { Eyebrow } from '../concept/eyebrow';

/**
 * The card pinned to the foot of the sidebar.
 *
 * The concept prints 44% here; ours prints whatever the arithmetic gives,
 * which for the document's own figures is 35% — see the spec's correction 3.
 * The footnote says "no date is forecast" because the source's "ca 6–8 weeks"
 * was the one number in it that nothing computes.
 */
export const GoalProgressCard: React.FC<{
  /** 0…1, or null when the client has no target. */
  fraction: number | null;
  targetBodyFat?: number;
  pointsDone?: number;
  pointsTotal?: number;
  onSetTarget?: () => void;
  /**
   * Framing. The default is the rail's — hairline, no ground. On a phone
   * there is no rail, so Home carries it as a card among cards.
   */
  className?: string;
}> = ({
  fraction,
  targetBodyFat,
  pointsDone,
  pointsTotal,
  onSetTarget,
  className = 'rounded-[10px] border border-divider p-3.5',
}) => {
  if (fraction === null || targetBodyFat === undefined) {
    return (
      <div className={className}>
        <Eyebrow className="text-[8.5px] tracking-[0.1em]">Client goal</Eyebrow>
        <p className="mt-2 text-xs leading-snug text-foreground-soft">
          No target set.
        </p>
        {onSetTarget && (
          <button
            type="button"
            onClick={onSetTarget}
            className="mt-3 text-[11px] font-medium text-primary hover:underline"
          >
            Set a target
          </button>
        )}
      </div>
    );
  }

  const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);

  return (
    <div data-testid="goal-progress" className={className}>
      <Eyebrow className="text-[8.5px] tracking-[0.1em]">Client goal</Eyebrow>
      <div className="mt-2 text-xs font-medium leading-snug">
        Reach {targetBodyFat} % body fat, hold lean mass.
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="tabular text-[26px] font-semibold leading-none tracking-[-0.02em]">
          {pct}%
        </span>
        <span className="text-[11px] text-muted-foreground">of the way</span>
      </div>
      <div className="mt-2 h-1 rounded-sm bg-divider">
        <div
          className="h-1 rounded-sm bg-primary transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="tabular mt-2 text-[10px] leading-snug text-muted-foreground">
        {pointsDone !== undefined && pointsTotal !== undefined
          ? `${pointsDone.toFixed(1)} of ${pointsTotal.toFixed(1)} points of body fat. `
          : ''}
        No date is forecast.
      </div>
    </div>
  );
};
